import * as React from "react";
import { Box, Typography } from "@mui/material";
import { range, sortBy } from "lodash";
import { fetchEnvs, fetchClients } from "../../http/api";
import http from "../../http/axios";
import type { Env, Client, selectedYear } from "../../store/features/envSlice";
import type { ClientFlow, DeploymentMode } from "./types";
import {
  GenerateUniqueButtons,
  GenerateMultipleButtons,
  GenerateMultipleRowButtons,
} from "./Buttons";

export interface RunSelectorProps {
  deployment: string;
  setDeployment: React.Dispatch<React.SetStateAction<string>>;
  deploymentMode: DeploymentMode;
  setDeploymentMode: React.Dispatch<React.SetStateAction<DeploymentMode>>;
  runPhase: string;
  setRunPhase: React.Dispatch<React.SetStateAction<string>>;
  clients: ClientFlow[];
  setClients: React.Dispatch<React.SetStateAction<ClientFlow[]>>;
  clientYears: selectedYear[];
  setClientYears: React.Dispatch<React.SetStateAction<selectedYear[]>>;
}

const RUN_PHASES = ["full", "raw_phase", "clean", "compute"] as const;

type SeasonRangeItem = { client: string; min_period: number; max_period: number };
type EnvSeasonsResponse =
  | { env_code: string; env_name: string; data: SeasonRangeItem[]; env_error_message?: string }
  | SeasonRangeItem[];

export const RunSelector: React.FC<RunSelectorProps> = ({
  deployment,
  setDeployment,
  deploymentMode,
  setDeploymentMode,
  runPhase,
  setRunPhase,
  clientYears,
  setClientYears,
  clients,
  setClients,
}) => {
  const [env, setEnv] = React.useState<Env | null>(null);
  const [envList, setEnvList] = React.useState<Env[]>([]);
  const [clientEnvList, setClientEnvList] = React.useState<Client[]>([]);
  // abréviation -> {min,max}
  const [seasonRanges, setSeasonRanges] = React.useState<Record<string, { min: number; max: number }>>({});

  const yearsBetween = (minY: number, maxY: number) => range(minY, maxY + 1).map(String);

  // 1) Charger la liste d'environnements (service A)
  React.useEffect(() => {
    fetchEnvs().then((response) => {
      setEnvList(sortBy(response.data.results, ["env_name"]));
    });
  }, []);

  // 2) Reset quand l'environnement change
  React.useEffect(() => {
    setRunPhase("");
    setClients([]);
    setClientYears([]);
    setClientEnvList([]);
    setDeploymentMode("");
    setSeasonRanges({});

    const nextEnv = envList.find((e) => e.env_name === deployment) ?? null;
    setEnv(nextEnv);
  }, [deployment, envList, setRunPhase, setClients, setClientYears, setDeploymentMode]);

  // 3) Charger la liste brute des clients de l'env courant
  React.useEffect(() => {
    if (!env?.base_url) return;
    let alive = true;
    fetchClients(env).then((response) => {
      if (!alive) return;
      setClientEnvList(response.data ?? []);
    });
    return () => {
      alive = false;
    };
  }, [env]);

  // 4) Construire la liste UI `clients` lorsqu'on a les clients bruts + ranges (on préserve `selected`)
  React.useEffect(() => {
    if (!deployment) return;

    setClients((prev) => {
      const prevById = new Map(prev.map((c) => [c.id, c]));
      return clientEnvList.map((_c) => {
        const prevItem = prevById.get(_c.id);
        const r = seasonRanges[_c.name];
        const years = r ? yearsBetween(r.min, r.max) : [];
        return {
          id: _c.id,
          name: _c.name, // abréviation
          allYears: years,
          selected: prevItem?.selected ?? false,
        };
      });
    });
    // on ne vide pas clientYears ici
  }, [clientEnvList, seasonRanges, deployment, setClients]);

  // 5) Purger les années orphelines quand la liste clients change
  React.useEffect(() => {
    if (!clientYears.length) return;
    const validIds = new Set(clients.map((c) => c.id));
    setClientYears((prev) => prev.filter((sy) => validIds.has(sy.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients]);

  // 6) Fetch des ranges (min/max) LAZILY quand de nouveaux clients sont sélectionnés
  React.useEffect(() => {
    if (!env?.env_code) return;
    const selectedAbbrevs = clients.filter((c) => c.selected).map((c) => c.name);
    if (!selectedAbbrevs.length) return;

    const needsFetch = selectedAbbrevs.some((abbr) => seasonRanges[abbr] === undefined);
    if (!needsFetch) return;

    let alive = true;
    http
      .post<EnvSeasonsResponse>("/clients-seasons/", { env_code: env.env_code })
      .then((res) => {
        if (!alive) return;
        const payload = res.data;
        const list: SeasonRangeItem[] = Array.isArray(payload) ? payload : (payload as any)?.data ?? [];

        setSeasonRanges((prev) => {
          const next = { ...prev };
          (list || []).forEach((row) => {
            if (!row || typeof row.client !== "string") return;
            const minp = Number(row.min_period);
            const maxp = Number(row.max_period);
            if (Number.isNaN(minp) || Number.isNaN(maxp)) return;
            if (next[row.client] === undefined) {
              next[row.client] = { min: minp, max: maxp };
            }
          });
          return next;
        });
      })
      .catch(() => {
        // on ignore, l'utilisateur pourra relancer en re-sélectionnant
      });

    return () => {
      alive = false;
    };
  }, [clients, env?.env_code, seasonRanges]);

  const isEnv =
    (env?.env_name && env.env_name.toLowerCase() === "development") ||
    (deployment && deployment.toLowerCase() === "development") ||
    (env?.env_name && env.env_name.toLowerCase() === "production") ||
    (deployment && deployment.toLowerCase() === "production");;

  const phaseAndClients = (
    <Box>
      {isEnv && (
        <>
          <Typography variant="h5">Deployment ("Heavy" is recommended)</Typography>
          <GenerateUniqueButtons
            array={["Light", "Heavy", "Deployment"]}
            value={deploymentMode}
            setValue={(v: string) => setDeploymentMode(v as DeploymentMode)}
          />
        </>
      )}

      <Typography variant="h5" sx={{ mt: 2 }}>
        Run Phase
      </Typography>
      <GenerateUniqueButtons array={[...RUN_PHASES]} value={runPhase} setValue={setRunPhase} />

      <Typography variant="h5" sx={{ mt: 2 }}>
        Clients
      </Typography>
      <GenerateMultipleButtons array={clients} setArray={setClients} />
    </Box>
  );

  const _deployments = envList.map((_env) => _env.env_name);

  return (
    <Box>
      <Typography variant="h5">Environnement</Typography>
      <GenerateUniqueButtons array={_deployments} value={deployment} setValue={setDeployment} />
      {deployment !== "" && phaseAndClients}
      <GenerateMultipleRowButtons array={clients} value={clientYears} setValue={setClientYears} />
    </Box>
  );
};
